import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../../ThemeContext';
import './ContactModal.scss';

const ContactModal = ({ show, onClose }) => {
  const { theme } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      console.log('Email would be sent to info@mindmade.ai with:', formData);

      setSubmitStatus('success');

      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', message: '' });
        setSubmitStatus(null);
        onClose();
      }, 2000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2,
      },
    },
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <AnimatePresence>
      {show && (
        <div className="contact-modal-wrapper">
          <motion.div
            className="contact-modal-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          <motion.div
            className={`contact-modal-container ${
              theme === 'dark' ? 'theme-dark' : 'theme-light'
            }`}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="contact-modal-content">
              {/* Header */}
              <div className="contact-modal-header">
                <div className="d-flex align-items-center mb-3">
                  <div className="contact-icon-wrapper me-3">
                    <i className="bi bi-headset"></i>
                  </div>
                  <div>
                    <h3 className="contact-modal-title mb-1">
                      Contact MindMade Support
                    </h3>
                    <p className="contact-modal-subtitle mb-0">
                      Send us a message at{' '}
                      <span className="email-link">info@mindmade.ai</span>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="contact-modal-close"
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="contact-input-group">
                      <input
                        type="text"
                        className="contact-input"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder=" "
                        required
                        disabled={isSubmitting}
                      />
                      <label htmlFor="name" className="contact-label">
                        <i className="bi bi-person me-2"></i>
                        Your Name
                      </label>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="contact-input-group">
                      <input
                        type="email"
                        className="contact-input"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder=" "
                        required
                        disabled={isSubmitting}
                      />
                      <label htmlFor="email" className="contact-label">
                        <i className="bi bi-envelope me-2"></i>
                        Email Address
                      </label>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="contact-input-group">
                      <input
                        type="text"
                        className="contact-input"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder=" "
                        disabled={isSubmitting}
                      />
                      <label htmlFor="company" className="contact-label">
                        <i className="bi bi-building me-2"></i>
                        Company (Optional)
                      </label>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="contact-input-group">
                      <textarea
                        className="contact-textarea"
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder=" "
                        required
                        disabled={isSubmitting}
                        rows="4"
                      ></textarea>
                      <label htmlFor="message" className="contact-label">
                        <i className="bi bi-chat-text me-2"></i>
                        Your Message
                      </label>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="contact-form-footer mt-4">
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="alert alert-success d-flex align-items-center mb-3"
                    >
                      <i className="bi bi-check-circle-fill me-2"></i>
                      <div>
                        <strong>Message sent successfully!</strong>
                        <br />
                        <small>We'll get back to you soon.</small>
                      </div>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="alert alert-danger d-flex align-items-center mb-3"
                    >
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <div>
                        <strong>Something went wrong.</strong>
                        <br />
                        <small>Please try again or email us directly.</small>
                      </div>
                    </motion.div>
                  )}

                  <div className="d-flex gap-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary flex-fill contact-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
